import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// speaking_club and individual_vip are legacy plans — no longer offered to new
// students (Kickstart + Elite Squad only, going forward), but kept here so
// existing students on those plans keep displaying correctly in analytics.
const PLAN_LABELS = {
  elite_squad:    'Elite Squad',
  individual_vip: 'Individual VIP',
  kickstart:      'Kickstart',
  speaking_club:  'Speaking Club',
};

const PLAN_COLORS = {
  elite_squad:    '#d97706',
  individual_vip: '#2563eb',
  kickstart:      '#16a349',
  speaking_club:  '#166534',
};

const PLAN_MONTHLY = {
  elite_squad:    299,
  individual_vip: 599,
  kickstart:      147,
  speaking_club:  99,
};

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Plus Jakarta Sans', -apple-system, sans-serif; background: #f7f7f5; color: #111; font-size: 14px; }

  .daq-app { display: flex; min-height: 100vh; }
  .daq-sidebar {
    width: 224px; background: #0c0c0c; border-right: 1px solid rgba(255,255,255,0.07);
    flex-shrink: 0; position: sticky; top: 0; height: 100vh; overflow-y: auto;
    display: flex; flex-direction: column;
  }
  .daq-main { flex: 1; padding: 32px 36px; overflow-x: hidden; max-width: 1100px; }

  .daq-logo { padding: 22px 20px 18px; border-bottom: 1px solid rgba(255,255,255,0.07); margin-bottom: 4px; }
  .daq-logo-mark {
    display: inline-flex; align-items: center; justify-content: center;
    width: 30px; height: 30px; background: #16a349; border-radius: 7px;
    font-size: 16px; font-weight: 800; color: #fff; margin-bottom: 10px; font-style: italic;
  }
  .daq-logo-name { font-size: 15px; font-weight: 600; color: #fff; line-height: 1.2; }
  .daq-logo-sub  { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 3px; }
  .daq-logo-tag  {
    display: inline-block; margin-top: 8px; font-size: 10px; font-weight: 600;
    color: #16a349; background: rgba(22,163,73,0.12); padding: 2px 8px;
    border-radius: 20px; letter-spacing: 0.04em;
  }
  .daq-nav-section {
    font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.22);
    letter-spacing: .1em; text-transform: uppercase; padding: 14px 20px 5px;
  }
  .daq-nav-item {
    display: flex; align-items: center; gap: 10px; padding: 9px 20px;
    font-size: 13px; color: rgba(255,255,255,0.5); cursor: pointer;
    border-left: 2px solid transparent; transition: all .15s; margin: 1px 0;
  }
  .daq-nav-item:hover { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.8); }
  .daq-nav-item.active {
    background: rgba(22,163,73,0.13); color: #fff; font-weight: 500;
    border-left-color: #16a349;
  }
  .daq-nav-icon { font-size: 14px; width: 18px; text-align: center; opacity: 0.7; }
  .daq-nav-item.active .daq-nav-icon { opacity: 1; }
  .daq-sidebar-footer { margin-top: auto; padding: 16px 20px; border-top: 1px solid rgba(255,255,255,0.07); }

  .daq-page { display: none; }
  .daq-page.active { display: block; }
  .daq-page-title { font-size: 21px; font-weight: 700; letter-spacing: -.4px; margin-bottom: 3px; }
  .daq-page-sub   { font-size: 13px; color: #5c5b58; margin-bottom: 24px; }

  .daq-card { background: #fff; border: 1px solid rgba(0,0,0,0.07); border-radius: 10px; padding: 20px; }
  .daq-card-title { font-size: 13px; font-weight: 600; margin-bottom: 14px; }
  .daq-card-sub   { font-size: 12px; color: #9d9c98; margin-top: -10px; margin-bottom: 14px; }

  .daq-g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .daq-g3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .daq-g4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; }
  .daq-g5 { display: grid; grid-template-columns: repeat(5,1fr); gap: 12px; }
  @media(max-width:900px) { .daq-g4,.daq-g5 { grid-template-columns:1fr 1fr; } .daq-g3 { grid-template-columns:1fr 1fr; } }
  @media(max-width:600px) { .daq-g2,.daq-g3,.daq-g4,.daq-g5 { grid-template-columns:1fr; } }
  .daq-gap { margin-bottom: 16px; }

  .daq-tile { background: #f0f0ed; border-radius: 6px; padding: 14px 16px; border: 1px solid rgba(0,0,0,0.07); }
  .daq-tile-label { font-size: 10px; color: #5c5b58; margin-bottom: 4px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
  .daq-tile-value { font-size: 22px; font-weight: 700; letter-spacing: -.5px; }
  .daq-tile-sub   { font-size: 11px; color: #9d9c98; margin-top: 3px; }

  .daq-badge { display: inline-flex; align-items: center; font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 20px; }
  .daq-badge-green  { background: #edfbf2; color: #0d6b35; }
  .daq-badge-red    { background: #fef2f2; color: #991b1b; }
  .daq-badge-amber  { background: #fffbeb; color: #92400e; }
  .daq-badge-blue   { background: #eff6ff; color: #1e40af; }
  .daq-badge-purple { background: #f5f3ff; color: #4c1d95; }
  .daq-badge-gray   { background: #f0f0ed; color: #5c5b58; }

  .daq-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid rgba(0,0,0,0.07); font-size: 13px; gap: 8px; }
  .daq-row:last-child { border-bottom: none; }
  .daq-row-label { color: #5c5b58; flex: 1; }
  .daq-row-value { font-weight: 500; }
  .daq-pos { color: #16a349; } .daq-neg { color: #dc2626; } .daq-warn { color: #d97706; }

  .daq-alert { border-radius: 6px; padding: 12px 14px; font-size: 13px; line-height: 1.5; border-left: 3px solid; margin-bottom: 10px; }
  .daq-alert-green  { background: #edfbf2; color: #0d6b35; border-color: #16a349; }
  .daq-alert-red    { background: #fef2f2; color: #991b1b; border-color: #dc2626; }
  .daq-alert-amber  { background: #fffbeb; color: #92400e; border-color: #d97706; }
  .daq-alert-blue   { background: #eff6ff; color: #1e40af; border-color: #2563eb; }

  .daq-slider-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
  .daq-slider-label { font-size: 12px; color: #5c5b58; min-width: 210px; line-height: 1.3; }
  .daq-slider-val { font-size: 12px; font-weight: 600; min-width: 72px; text-align: right; color: #16a349; background: rgba(22,163,73,0.1); padding: 2px 8px; border-radius: 4px; }
  input[type=range] { flex: 1; accent-color: #16a349; cursor: pointer; }
  select, input[type=text], input[type=number] {
    border: 1px solid rgba(0,0,0,0.13); border-radius: 6px; padding: 7px 10px;
    font-size: 13px; color: #111; background: #fff; width: 100%;
    outline: none; font-family: inherit;
  }
  select:focus, input:focus { border-color: #16a349; box-shadow: 0 0 0 3px rgba(22,163,73,0.1); }

  .daq-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .daq-tbl th { font-size: 10px; font-weight: 600; color: #5c5b58; text-align: left; padding: 8px 8px 8px 0; border-bottom: 2px solid rgba(0,0,0,0.1); text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; }
  .daq-tbl td { padding: 9px 8px 9px 0; border-bottom: 1px solid rgba(0,0,0,0.07); vertical-align: middle; }
  .daq-tbl tr:last-child td { border-bottom: none; }
  .daq-tbl tbody tr:hover td { background: #f7f7f5; }

  .daq-pb-bg { background: #f0f0ed; border-radius: 3px; height: 5px; overflow: hidden; }
  .daq-pb    { height: 5px; border-radius: 3px; }

  .daq-chart { width: 100%; }

  hr { border: none; border-top: 1px solid rgba(0,0,0,0.07); margin: 16px 0; }

  .daq-sc { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(0,0,0,0.07); }
  .daq-sc:last-child { border-bottom: none; }
  .daq-av { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }

  .daq-login-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(160deg,#0c0c0c 0%,#111827 60%,#1a2a6c 100%); }
  .daq-login-box { background: #fff; padding: 52px 40px; border-radius: 20px; width: 400px; max-width: 92vw; box-shadow: 0 40px 80px rgba(0,0,0,0.4); text-align: center; }
  .daq-login-mark { width: 56px; height: 56px; background: #16a349; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; font-size: 1.6rem; font-weight: 900; color: #fff; font-style: italic; }
  .daq-login-btn { width: 100%; padding: 14px; background: #0c0c0c; color: #fff; border: none; border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; margin-top: 12px; transition: opacity .15s; }
  .daq-login-btn:hover { opacity: 0.85; }
  .daq-login-input { width: 100%; padding: 12px 14px; border: 1.5px solid #e5e5e3; border-radius: 8px; font-size: 14px; font-family: inherit; outline: none; margin-bottom: 10px; }
  .daq-login-input:focus { border-color: #16a349; }
  .daq-err { color: #dc2626; font-size: 13px; margin-top: 10px; }

  .daq-live { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #16a349; }
  .daq-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #16a349; animation: daq-pulse 2s ease-in-out infinite; }
  @keyframes daq-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.3)} }

  .daq-skel { background: linear-gradient(90deg,#f0f0ed 25%,#e8e8e5 50%,#f0f0ed 75%); background-size: 200% 100%; animation: daq-skel 1.5s ease-in-out infinite; border-radius: 6px; }
  @keyframes daq-skel { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  @media(max-width:768px) {
    .daq-app { flex-direction: column; }
    .daq-sidebar { width:100%; height:auto; position:static; flex-direction:row; border-right:none; border-bottom:1px solid rgba(255,255,255,0.07); }
    .daq-logo,.daq-nav-section,.daq-sidebar-footer { display:none; }
    .daq-main { padding: 16px; }
  }
`;

const fmt  = n => 'R$' + Math.round(n).toLocaleString('pt-BR');
const fmtK = n => Math.abs(n) >= 1000 ? 'R$' + (n / 1000).toFixed(1).replace('.', ',') + 'k' : fmt(n);
const pct  = n => Math.round(n) + '%';
const sgn  = n => n >= 0 ? 'daq-pos' : 'daq-neg';
const bCls = m => m < 0 ? 'daq-badge-red' : m < 35 ? 'daq-badge-amber' : 'daq-badge-green';

const slotCost   = (sess, rate, pH, aH) => sess * (1 + pH + aH) * rate;
const esGrpEcon  = (slots, avg, price, rate, pH, aH, rH, cH) => {
  const sess = slots * 4;
  const sc   = slotCost(sess, rate, pH, aH);
  const n    = slots * avg;
  const cost = sc + n * (rH + cH) * rate;
  const rev  = n * price;
  const gp   = rev - cost;
  const cps  = n > 0 ? cost / n : cost;
  return { rev, cost, gp, margin: rev > 0 ? gp / rev * 100 : 0, n, sc, cps, gpps: price - cps };
};
// Elite Squad individual phase (months 1-2):
// 1h 1-on-1 lesson/week + 2h Speaking Club/week (shared across scSize students) + prep/week
const esIndEcon = (price, rate, pH, aH, rH, cH, scHrsPerWeek, scSize, lessonHrsPerWeek, prepHrsPerWeek) => {
  const weeksPerMonth = 4;
  const lessonHrsMonth = lessonHrsPerWeek * weeksPerMonth;       // 1-on-1, full cost to this student
  const scHrsMonth     = (scHrsPerWeek / scSize) * weeksPerMonth; // Speaking Club, shared across group
  const prepHrsMonth   = prepHrsPerWeek * weeksPerMonth;          // prep, reusable but counted per student here
  const contactHrs     = lessonHrsMonth + scHrsMonth;
  const h = contactHrs + prepHrsMonth + 4 * aH + rH + cH; // aH/rH/cH still apply as monthly overhead
  const cost = h * rate;
  return { cost, gp: price - cost, margin: (price - cost) / price * 100, h, lessonHrsMonth, scHrsMonth, prepHrsMonth };
};
const ksEcon = (groups, avg, price, rate, pH, aH, rH, cH) => {
  const sessPerMonth = 2;
  const cpg  = slotCost(sessPerMonth * 2, rate, pH, aH);
  const n    = groups * avg;
  const cost = groups * cpg + n * (rH + cH) * rate;
  const rev  = n * price;
  const gp   = rev - cost;
  const cps  = n > 0 ? cost / n : 0;
  return { rev, cost, gp, margin: rev > 0 ? gp / rev * 100 : 0, n, cps, gpps: price - cps };
};
const vipEcon    = (price, rate, pH, aH, rH, cH) => {
  const h = 4 * (1 + pH) + 4 * aH + rH + cH;
  const cost = h * rate;
  return { cost, gp: price - cost, margin: (price - cost) / price * 100, h };
};

const DR = ({ label, value, cls = '' }) => (
  <div className="daq-row">
    <span className="daq-row-label">{label}</span>
    <span className={`daq-row-value ${cls}`} dangerouslySetInnerHTML={{ __html: value }} />
  </div>
);

const MCard = ({ title, children, margin, badge }) => (
  <div>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 12 }}>
      <div className="daq-card-title" style={{ margin: 0 }}>{title}</div>
      <span className={`daq-badge ${bCls(margin)}`}>{pct(margin)} margin</span>
    </div>
    {badge && <div style={{ marginBottom: 10 }}><span className="daq-badge daq-badge-purple">{badge}</span></div>}
    {children}
  </div>
);

const SliderRow = ({ label, min, max, step, value, onChange, display }) => (
  <div className="daq-slider-row">
    <span className="daq-slider-label">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)} />
    <span className="daq-slider-val">{display}</span>
  </div>
);

const COLORS = { green:'#16a349', red:'#dc2626', amber:'#d97706', blue:'#2563eb', purple:'#7c3aed', gray:'#9d9c98' };
const tooltipStyle = {
  contentStyle: { background:'#fff', border:'1px solid rgba(0,0,0,0.1)', borderRadius: 8, fontSize: 12, fontFamily:'Plus Jakarta Sans,sans-serif' },
  labelStyle: { fontWeight: 700, color: '#111' },
};

export default function AdminData() {
  const [user,      setUser]      = useState(null);
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [authErr,   setAuthErr]   = useState('');
  const [authLoad,  setAuthLoad]  = useState(false);

  const [dbStudents,      setDbStudents]      = useState([]);
  const [dbCancellations, setDbCancellations] = useState([]);
  const [dbPnl,           setDbPnl]           = useState([]);
  const [liveLoading,     setLiveLoading]     = useState(false);
  const [lastFetched,     setLastFetched]     = useState(null);

  const [tab, setTab] = useState('overview');

  const [rate,    setRate]    = useState(35);
  const [prepMin, setPrepMin] = useState(40);
  const [adminMin,setAdminMin]= useState(20);
  const [repMin,  setRepMin]  = useState(30);
  const [commsMin,setCommsMin]= useState(30);

  const [esiLessonHrs, setEsiLessonHrs] = useState(1);
  const [esiScHrs,     setEsiScHrs]     = useState(2);
  const [esiScSize,    setEsiScSize]    = useState(3);
  const [esiPrepHrs,   setEsiPrepHrs]   = useState(0);

  const [esiN,  setEsiN]  = useState(2);
  const [esN,   setEsN]   = useState(7);
  const [esSlots,setEsSlots]=useState(3);
  const [ksG,   setKsG]   = useState(1);
  const [ksF,   setKsF]   = useState(3);
  const [vipN,  setVipN]  = useState(0);
  const [legRev,setLegRev]= useState(3500);
  const [sal,   setSal]   = useState(5435);
  const [mkt,   setMkt]   = useState(1666);
  const [tax,   setTax]   = useState(746);
  const [oth,   setOth]   = useState(543);

  const [slSlots, setSlSlots] = useState(4);
  const [slStu,   setSlStu]   = useState(12);
  const [slRate,  setSlRate]  = useState(35);
  const [slPrep,  setSlPrep]  = useState(40);

  const [lChurn,  setLChurn]  = useState(22);
  const [lCmo,    setLCmo]    = useState(5);
  const [lRenew,  setLRenew]  = useState(40);

  const [cSpend,  setCSpend]  = useState(1500);
  const [cEs,     setCEs]     = useState(2);
  const [cKs,     setCKs]     = useState(1);
  const [cVip,    setCVip]    = useState(0);
  const [cLeg,    setCLeg]    = useState(1);

  const [prKs,    setPrKs]    = useState(147);
  const [prKsN,   setPrKsN]   = useState(4);
  const [prEs,    setPrEs]    = useState(299);
  const [prEsF,   setPrEsF]   = useState(3);
  const [prEsS,   setPrEsS]   = useState(4);
  const [prVip,   setPrVip]   = useState(599);
  const [prDisc,  setPrDisc]  = useState(0);
  const [prSvc,   setPrSvc]   = useState('ks');

  const [beFixed, setBeFixed] = useState(8390);
  const [beSlots, setBeSlots] = useState(4);

  const handleLogin = async e => {
    e.preventDefault();
    setAuthLoad(true); setAuthErr('');
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) { setAuthErr('Login falhou: ' + error.message); setAuthLoad(false); return; }
    const { data: stu } = await supabase.from('students').select('role').eq('id', data.user.id).single();
    if (stu?.role === 'founder' || stu?.role === 'admin') {
      setUser(data.user);
    } else {
      await supabase.auth.signOut();
      setAuthErr('Acesso restrito. Esta área é exclusiva para fundadores.');
    }
    setAuthLoad(false);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); setUser(null); };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      supabase.from('students').select('role').eq('id', session.user.id).single()
        .then(({ data }) => {
          if (data?.role === 'founder' || data?.role === 'admin') setUser(session.user);
        });
    });
  }, []);

  const fetchLive = useCallback(async () => {
    setLiveLoading(true);
    const [{ data: stu }, { data: canc }, { data: pnlRows }] = await Promise.all([
      supabase.from('students').select('id,full_name,email,plan,level,contract_start,contract_end,is_trial,phone,created_at,cohort,phase,monthly_price').order('created_at', { ascending: true }),
      supabase.from('cancellations').select('student_email,cancelled_at,penalty_collected,penalty_amount'),
      supabase.from('monthly_pnl').select('*').order('year').order('month_order'),
    ]);
    setDbStudents(stu || []);
    setDbCancellations(canc || []);
    setDbPnl(pnlRows || []);
    setLastFetched(new Date());
    setLiveLoading(false);
  }, []);

  useEffect(() => { if (user) fetchLive(); }, [user, fetchLive]);

  const today = new Date(); today.setUTCHours(0,0,0,0);
  const activeStudents  = dbStudents.filter(s => !s.is_trial && s.plan && (s.contract_end === null || new Date(s.contract_end) >= today));
  const trialStudents   = dbStudents.filter(s => s.is_trial);
  const planGroups      = Object.keys(PLAN_LABELS).map(p => ({
    plan: p, label: PLAN_LABELS[p],
    count: activeStudents.filter(s => s.plan === p).length,
    color: PLAN_COLORS[p],
    mrr:   activeStudents.filter(s => s.plan === p).reduce((a, s) => a + (s.monthly_price || PLAN_MONTHLY[p]), 0),
  }));
  const totalMRR        = planGroups.reduce((a, p) => a + p.mrr, 0);
  const totalStudents   = dbStudents.length;
  const churnedCount    = [...new Set(dbCancellations.map(c => c.student_email))].length;
  const churnRate       = totalStudents > 0 ? Math.round(churnedCount / totalStudents * 100) : 0;
  const esActive        = activeStudents.filter(s => s.plan === 'elite_squad').length;
  const penaltyTotal    = dbCancellations.reduce((a, c) => a + (c.penalty_collected ? (c.penalty_amount || 0) : 0), 0);
  const penaltyPending  = dbCancellations.filter(c => !c.penalty_collected).length;

  // Live Elite Squad cohorts — derived from students table, auto-updates
  // when a student's contract ends, leaves, or a new cancellation is logged.
  const eliteSquadByCohort = (() => {
    const groups = {};
    dbStudents.forEach(s => {
      if (s.plan !== 'elite_squad' || !s.cohort) return;
      if (!groups[s.cohort]) groups[s.cohort] = [];
      const left = dbCancellations.some(c => c.student_email === s.email);
      groups[s.cohort].push({
        name: s.full_name,
        phase: s.phase || 'individual',
        level: s.level,
        status: left ? 'left' : 'active',
      });
    });
    return groups;
  })();
  const cohortNames = Object.keys(eliteSquadByCohort).sort();

  const acqByMonth = (() => {
    const map = {};
    dbStudents.forEach(s => {
      if (!s.created_at) return;
      const mo = s.created_at.substring(0, 7);
      map[mo] = (map[mo] || 0) + 1;
    });
    return Object.entries(map).sort().slice(-8).map(([m, n]) => ({
      month: m.substring(5) + '/' + m.substring(2, 4), students: n,
    }));
  })();

  const planPieData = planGroups.filter(p => p.count > 0).map(p => ({
    name: p.label, value: p.mrr, count: p.count, color: p.color,
  }));

  const pH = prepMin / 60, aH = adminMin / 60, rH = repMin / 60, cH = commsMin / 60;

  const mKs  = ksEcon(1, 4, 147, rate, pH, aH, rH, cH);
  const mVip = vipEcon(599, rate, pH, aH, rH, cH);
  const mEsI = esIndEcon(299, rate, pH, aH, rH, cH, esiScHrs, esiScSize, esiLessonHrs, esiPrepHrs);
  const mEsG = esGrpEcon(4, 3, 299, rate, pH, aH, rH, cH);

  const marginData = [
    { name: 'Kickstart',     revenue: 147, cost: Math.round(mKs.cps),  gp: Math.round(mKs.gpps)  },
    { name: 'VIP',           revenue: 599, cost: Math.round(mVip.cost), gp: Math.round(mVip.gp)   },
    { name: 'ES Ind (mo1-2)',revenue: 299, cost: Math.round(mEsI.cost), gp: Math.round(mEsI.gp)   },
    { name: 'ES Group',      revenue: 299, cost: Math.round(mEsG.cps),  gp: Math.round(mEsG.gpps) },
  ];

  const pnl = (() => {
    const avg    = esN > 0 ? esN / esSlots : 0;
    const esgE   = esGrpEcon(esSlots, avg, 299, rate, pH, aH, rH, cH);
    const esiE   = esIndEcon(299, rate, pH, aH, rH, cH, esiScHrs, esiScSize, esiLessonHrs, esiPrepHrs);
    const ksE    = ksEcon(ksG, ksF, 147, rate, pH, aH, rH, cH);
    const vipE   = vipEcon(599, rate, pH, aH, rH, cH);
    const rESI   = esiN * 299,   gpESI = esiN * esiE.gp;
    const rES    = esgE.rev,     gpES  = esgE.gp;
    const rKS    = ksE.rev,      gpKS  = ksE.gp;
    const rVIP   = vipN * 599,   gpVIP = vipN * vipE.gp;
    const tRev   = rESI + rES + rKS + rVIP + legRev;
    const tTeach = esiN * esiE.cost + esgE.cost + ksE.cost + vipN * vipE.cost;
    const gross  = gpESI + gpES + gpKS + gpVIP + legRev * 0.55;
    const tFixed = sal + mkt + tax + oth;
    const net    = gross - tFixed;
    return { tRev, tTeach, gross, tFixed, net, nm: tRev > 0 ? net / tRev * 100 : 0 };
  })();

  const pnlWaterfall = [
    { name: 'ES individual', v: esiN * 299,             fill: '#5DCAA5' },
    { name: 'ES group',      v: esN * 299,              fill: '#16a349' },
    { name: 'Kickstart',     v: ksG * ksF * 147,        fill: '#97C459' },
    { name: 'VIP',           v: vipN * 599,             fill: '#2563eb' },
    { name: 'Legacy',        v: legRev,                 fill: '#7c3aed' },
    { name: 'Teacher cost',  v: -Math.round(pnl.tTeach),fill: '#dc2626' },
    { name: 'Fixed costs',   v: -Math.round(pnl.tFixed),fill: '#991b1b' },
    { name: 'Net profit',    v: Math.round(pnl.net),    fill: pnl.net >= 0 ? '#2563eb' : '#dc2626' },
  ];

  const ltvEs  = (() => {
    const gpI = mEsI.gp, gpG = mEsG.gpps;
    const full = gpI * 2 + gpG * 10;
    const gpC  = lCmo <= 2 ? gpI * lCmo : gpI * 2 + gpG * (lCmo - 2);
    const exp  = full * (1 - lChurn / 100) + gpC * (lChurn / 100);
    const be   = gpI >= 0 ? 1 : Math.min(12, Math.ceil(Math.abs(gpI * 2) / gpG) + 2);
    const cum  = Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      return { mo: 'Mo ' + m, gp: Math.round(m <= 2 ? gpI * m : gpI * 2 + gpG * (m - 2)) };
    });
    return { full, exp, be, cum };
  })();

  const ltvKs = (() => {
    const full = mKs.gpps * 2;
    return { full };
  })();

  const slAvg = slStu / slSlots;
  const slE   = esGrpEcon(slSlots, slAvg, 299, slRate, slPrep / 60, 20 / 60, 30 / 60, 30 / 60);
  const fillData = Array.from({ length: 5 }, (_, i) => {
    const e = esGrpEcon(slSlots, i + 1, 299, slRate, slPrep / 60, 20 / 60, 30 / 60, 30 / 60);
    return { name: (i + 1) + '/slot', rev: Math.round(e.rev), cost: Math.round(e.cost), gp: Math.round(e.gp) };
  });

  const cTotal   = cEs + cKs + cVip + cLeg;
  const blendCAC = cTotal > 0 ? cSpend / cTotal : 0;
  const esGP12   = mEsI.gp * 2 + mEsG.gpps * 10;
  const ksGP2    = mKs.gpps * 2;
  const vipGP3   = mVip.gp * 3;

  const prKsE  = ksEcon(1, prKsN, prKs, rate, pH, aH, rH, cH);
  const prEsE  = esGrpEcon(prEsS, prEsF, prEs, rate, pH, aH, rH, cH);
  const prVipE = vipEcon(prVip, rate, pH, aH, rH, cH);
  const prMap  = {
    ks:  { p: prKs,  cost: prKsE.cps,   name: 'Kickstart' },
    es:  { p: prEs,  cost: prEsE.cps,   name: 'Elite Squad' },
    vip: { p: prVip, cost: prVipE.cost, name: 'Individual VIP' },
  };
  const prDs   = prMap[prSvc];
  const dP     = prDs.p * (1 - prDisc / 100), dGP = dP - prDs.cost;

  const beKS   = mKs.gpps  > 0 ? Math.ceil(beFixed / mKs.gpps / 5)  : Infinity;
  const beESF  = mEsG.gpps > 0 ? Math.ceil(beFixed / mEsG.gpps)     : Infinity;
  const beESH  = (() => { const e = esGrpEcon(beSlots, 2.5, 299, rate, pH, aH, rH, cH); return e.gpps > 0 ? Math.ceil(beFixed / e.gpps) : Infinity; })();
  const beVIP  = mVip.gp   > 0 ? Math.ceil(beFixed / mVip.gp)       : Infinity;
  const beLineData = Array.from({ length: 35 }, (_, i) => {
    const x = i + 1;
    const eF = esGrpEcon(beSlots, 5, 299, rate, pH, aH, rH, cH);
    return { x, ks: Math.round(mKs.gpps * x), esF: Math.round(eF.gpps * x), vip: Math.round(mVip.gp * x), fixed: Math.round(beFixed) };
  });

  if (!user) return (
    <>
      <style>{S}</style>
      <div className="daq-login-wrap">
        <div className="daq-login-box">
          <div className="daq-login-mark">Q</div>
          <h2 style={{ fontWeight: 800, color: '#111', marginBottom: 6, fontSize: '1.5rem' }}>Founders Dashboard</h2>
          <p style={{ color: '#9d9c98', fontSize: '0.88rem', marginBottom: 28 }}>Idiomas Quevedo · Acesso restrito</p>
          <form onSubmit={handleLogin}>
            <input className="daq-login-input" type="email" placeholder="E-mail" value={email} onChange={e => setEmail(e.target.value)} required />
            <input className="daq-login-input" type="password" placeholder="Senha" value={password} onChange={e => setPassword(e.target.value)} required />
            <button className="daq-login-btn" type="submit" disabled={authLoad}>{authLoad ? 'Verificando...' : 'Entrar'}</button>
            {authErr && <p className="daq-err">{authErr}</p>}
          </form>
          <p style={{ marginTop: 20, fontSize: 12, color: '#c0c0bb' }}>
            Professores: acesse em <a href="/admin" style={{ color: '#16a349' }}>/admin</a>
          </p>
        </div>
      </div>
    </>
  );

  const NAV = [
    { section: 'Visão geral' },
    { id: 'overview',  label: 'Overview',         icon: '◈' },
    { id: 'students',  label: 'Students',          icon: '◎' },
    { section: 'Economia' },
    { id: 'margins',   label: 'Margins',           icon: '◧' },
    { id: 'ltv',       label: 'LTV & contracts',   icon: '◷' },
    { id: 'pnl',       label: 'P&L tracker',       icon: '◫' },
    { section: 'Estratégia' },
    { id: 'slots',     label: 'Slot utilization',  icon: '◰' },
    { id: 'cac',       label: 'Acquisition cost',  icon: '◎' },
    { id: 'pricing',   label: 'Pricing & promos',  icon: '◈' },
    { id: 'breakeven', label: 'Break-even',         icon: '◎' },
  ];

  return (
    <>
      <style>{S}</style>
      <div className="daq-app">

        <aside className="daq-sidebar">
          <div className="daq-logo">
            <div className="daq-logo-mark">Q</div>
            <div className="daq-logo-name">Idiomas Quevedo</div>
            <div className="daq-logo-sub">Dashboard interno · 2026</div>
            <div className="daq-logo-tag">⚡ Founders only</div>
          </div>
          {NAV.map((n, i) => n.section
            ? <div key={i} className="daq-nav-section">{n.section}</div>
            : (
              <div key={n.id} className={`daq-nav-item ${tab === n.id ? 'active' : ''}`} onClick={() => setTab(n.id)}>
                <span className="daq-nav-icon">{n.icon}</span>
                <span>{n.label}</span>
              </div>
            )
          )}
          <div className="daq-sidebar-footer">
            {lastFetched && (
              <div className="daq-live" style={{ marginBottom: 10 }}>
                <span className="daq-live-dot" />
                Live · {lastFetched.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <button onClick={fetchLive} disabled={liveLoading} style={{ width:'100%', padding:'7px', background:'rgba(22,163,73,0.12)', color:'#16a349', border:'1px solid rgba(22,163,73,0.2)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor:'pointer', fontFamily:'inherit', marginBottom: 8 }}>
              {liveLoading ? '↻ Atualizando...' : '↻ Atualizar dados'}
            </button>
            <button onClick={handleLogout} style={{ width:'100%', padding:'7px', background:'transparent', color:'rgba(255,255,255,0.35)', border:'1px solid rgba(255,255,255,0.1)', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor:'pointer', fontFamily:'inherit' }}>
              Sair
            </button>
          </div>
        </aside>

        <main className="daq-main">

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div className="daq-page active">
              <div className="daq-page-title">Business overview</div>
              <div className="daq-page-sub">Dados ao vivo do Supabase · {liveLoading ? 'Carregando...' : `${activeStudents.length} alunos ativos`}</div>

              <div className="daq-g5 daq-gap">
                {[
                  { label:'Alunos ativos',    value: liveLoading ? '…' : activeStudents.length, sub:'todas as modalidades' },
                  { label:'MRR estimado',     value: liveLoading ? '…' : fmtK(totalMRR),        sub:'subscriptions ativas' },
                  { label:'Elite Squad',      value: liveLoading ? '…' : esActive,               sub:'contratos R$299/mo' },
                  { label:'Taxa de churn',    value: liveLoading ? '…' : churnRate + '%',        sub:'cancelamentos / total' },
                  { label:'Em trial',         value: liveLoading ? '…' : trialStudents.length,  sub:'ainda não convertidos' },
                ].map(t => (
                  <div key={t.label} className="daq-tile">
                    <div className="daq-tile-label">{t.label}</div>
                    <div className="daq-tile-value">{t.value}</div>
                    <div className="daq-tile-sub">{t.sub}</div>
                  </div>
                ))}
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Receita mensal por plano <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span></div>
                  <div className="daq-chart" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={planPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, count }) => `${name} (${count})`} labelLine={false} fontSize={11}>
                          {planPieData.map((p, i) => <Cell key={i} fill={p.color} />)}
                        </Pie>
                        <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ display:'flex', gap: 12, marginTop: 8, flexWrap:'wrap' }}>
                    {planGroups.map(p => (
                      <div key={p.plan} style={{ fontSize: 12, display:'flex', alignItems:'center', gap: 5 }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color, flexShrink:0, display:'inline-block' }} />
                        {p.label}: <strong>{p.count}</strong> · {fmt(p.mrr)}/mo
                      </div>
                    ))}
                  </div>
                </div>

                <div className="daq-card">
                  <div className="daq-card-title">Novos alunos por mês <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span></div>
                  <div className="daq-chart" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={acqByMonth} barSize={24}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip {...tooltipStyle} />
                        <Bar dataKey="students" fill={COLORS.green} radius={[4,4,0,0]} name="Novos alunos" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Snapshot do modelo</div>
                  <DR label="Elite Squad ativos"        value={esActive + ''} />
                  <DR label="Individual VIP ativos"     value={activeStudents.filter(s => s.plan==='individual_vip').length + ''} />
                  <DR label="Kickstart ativos"          value={activeStudents.filter(s => s.plan==='kickstart').length + ''} />
                  <DR label="Em trial"                  value={trialStudents.length + ''} />
                  <DR label="Total cadastrados"         value={totalStudents + ''} />
                  <DR label="Cancelamentos registrados" value={String(churnedCount)} cls="daq-neg" />
                  <DR label="Multas cobradas"           value={fmt(penaltyTotal)} cls="daq-pos" />
                  {penaltyPending > 0 && <DR label="Multas pendentes" value={penaltyPending + ' sem registro'} cls="daq-warn" />}
                </div>

                <div className="daq-card">
                  <div className="daq-card-title">Alertas</div>
                  {churnedCount > 0 && penaltyTotal > 0 && (
                    <div className="daq-alert daq-alert-green">
                      <strong>{churnedCount} cancelamento(s) registrados.</strong> {fmt(penaltyTotal)} em multas rescisórias já cobradas.
                    </div>
                  )}
                  {penaltyPending > 0 && (
                    <div className="daq-alert daq-alert-amber">
                      <strong>{penaltyPending} cancelamento(s) sem multa registrada.</strong> Verificar no portal /admin.
                    </div>
                  )}
                  {trialStudents.length > 0 && (
                    <div className="daq-alert daq-alert-blue">
                      <strong>{trialStudents.length} aluno{trialStudents.length>1?'s':''} em trial</strong> ainda não convertidos. Acompanhar no portal /admin.
                    </div>
                  )}
                  <div className="daq-alert daq-alert-amber">
                    <strong>Transição de modelo:</strong> receita de assinatura ainda não cobre toda a estrutura sozinha — ver P&L.
                  </div>
                  <div className="daq-alert daq-alert-green">
                    <strong>Kickstart</strong> é o novo funil de entrada — grupo, 8 semanas, R$147/mês. Meta: converter para Elite Squad na semana 8.
                  </div>
                  {esActive > 0 && (
                    <div className="daq-alert daq-alert-green">
                      <strong>Elite Squad</strong> é nossa base de receita recorrente mais previsível. Priorizar novas turmas.
                    </div>
                  )}
                </div>
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">P&L histórico — Jan a Jun 2026 <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span></div>
                <div className="daq-chart" style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dbPnl}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="revenue" name="Receita" fill="rgba(22,163,73,0.2)" stroke={COLORS.green} strokeWidth={2} radius={[4,4,0,0]} />
                      <Bar dataKey="profit"  name="Lucro"   radius={[4,4,0,0]}>
                        {dbPnl.map((row, i) => <Cell key={i} fill={row.profit >= 0 ? COLORS.blue : COLORS.red} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* STUDENTS */}
          {tab === 'students' && (
            <div className="daq-page active">
              <div className="daq-page-title">Students</div>
              <div className="daq-page-sub">Dados ao vivo do Supabase · {activeStudents.length} ativos</div>

              <div className="daq-card daq-gap" style={{ overflowX:'auto' }}>
                <table className="daq-tbl">
                  <thead>
                    <tr><th>Nome</th><th>Plano</th><th>Nível</th><th>Início contrato</th><th>Fim contrato</th><th>Preço/mês</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {liveLoading
                      ? Array.from({length:5}).map((_,i)=>(
                          <tr key={i}>{Array.from({length:6}).map((_,j)=>(
                            <td key={j}><div className="daq-skel" style={{ height:16, width:'80%' }} /></td>
                          ))}</tr>
                        ))
                      : activeStudents.map(s => {
                          const pc = PLAN_COLORS[s.plan] || '#9d9c98';
                          return (
                            <tr key={s.id}>
                              <td>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.full_name}</div>
                                <div style={{ fontSize: 11, color: '#9d9c98' }}>{s.email}</div>
                              </td>
                              <td>
                                <span className="daq-badge" style={{ background: pc + '22', color: pc, border: `1px solid ${pc}44` }}>
                                  {PLAN_LABELS[s.plan] || s.plan}
                                </span>
                              </td>
                              <td style={{ fontSize: 12, color: '#5c5b58' }}>{s.level || '—'}</td>
                              <td style={{ fontSize: 12 }}>{s.contract_start ? new Date(s.contract_start).toLocaleDateString('pt-BR',{timeZone:'UTC'}) : '—'}</td>
                              <td style={{ fontSize: 12 }}>{s.contract_end   ? new Date(s.contract_end).toLocaleDateString('pt-BR',{timeZone:'UTC'})   : '—'}</td>
                              <td style={{ fontSize: 12, fontWeight: 600 }}>{s.monthly_price ? <span style={{ color: '#16a349' }}>R${s.monthly_price}</span> : `R$${PLAN_MONTHLY[s.plan] || '—'}`}</td>
                              <td><span className="daq-badge daq-badge-green">Ativo</span></td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
                {!liveLoading && activeStudents.length === 0 && (
                  <div style={{ textAlign:'center', padding: 32, color:'#9d9c98' }}>Nenhum aluno ativo encontrado.</div>
                )}
              </div>

              <div className="daq-g2 daq-gap">
                {cohortNames.length === 0 && (
                  <div className="daq-card" style={{ gridColumn: 'span 2' }}>
                    <div style={{ textAlign:'center', padding: 24, color:'#9d9c98' }}>
                      Nenhum cohort Elite Squad encontrado. Defina o campo <code>cohort</code> na tabela students.
                    </div>
                  </div>
                )}
                {cohortNames.map(cohortName => {
                  const members = eliteSquadByCohort[cohortName];
                  const colorPalette = [['#edfbf2','#0d6b35'],['#eff6ff','#1e40af'],['#f5f3ff','#4c1d95'],['#fffbeb','#92400e']];
                  return (
                    <div className="daq-card" key={cohortName}>
                      <div className="daq-card-title">
                        Elite Squad — Cohort {cohortName} <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span>
                      </div>
                      {members.map((s, i) => {
                        const [bg, tc] = colorPalette[i % colorPalette.length];
                        return (
                          <div key={s.name} className="daq-sc">
                            <div className="daq-av" style={{ background: bg, color: tc }}>{s.name.split(' ').map(w=>w[0]).join('').slice(0,2)}</div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13, fontWeight:600 }}>{s.name}</div>
                              <div style={{ fontSize:11, color:'#9d9c98' }}>
                                <span style={{ width:7, height:7, borderRadius:'50%', background: s.phase==='individual'?'#d97706':'#16a349', display:'inline-block', marginRight:4 }} />
                                {s.phase === 'individual' ? 'Individual phase' : 'Group phase'} · {s.level || '—'}
                              </div>
                            </div>
                            <span className={`daq-badge ${s.status==='active' ? 'daq-badge-green' : 'daq-badge-red'}`}>
                              {s.status === 'active' ? 'Ativo' : 'Saiu'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* MARGINS */}
          {tab === 'margins' && (
            <div className="daq-page active">
              <div className="daq-page-title">Margins</div>
              <div className="daq-page-sub">Custo real = horas de contato + prep + admin + relatórios + comms.</div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Premissas de custo</div>
                <SliderRow label="Taxa horária do professor (R$)"       min={25} max={80}  step={1} value={rate}     onChange={setRate}     display={'R$' + rate} />
                <SliderRow label="Prep por hora de contato (min)"       min={10} max={90}  step={5} value={prepMin}  onChange={setPrepMin}  display={prepMin + ' min'} />
                <SliderRow label="Admin pós-sessão por sessão (min)"    min={5}  max={45}  step={5} value={adminMin} onChange={setAdminMin} display={adminMin + ' min'} />
                <SliderRow label="Relatório mensal por aluno (min)"     min={0}  max={60}  step={5} value={repMin}   onChange={setRepMin}   display={repMin + ' min'} />
                <SliderRow label="WhatsApp / comms por aluno/mês (min)" min={10} max={90}  step={5} value={commsMin} onChange={setCommsMin} display={commsMin + ' min'} />
                <hr />
                <div className="daq-card-title" style={{ marginBottom: 10 }}>Elite Squad — fase individual (estrutura semanal)</div>
                <SliderRow label="Aula individual (h/semana)"       min={0.5} max={3}  step={0.5} value={esiLessonHrs} onChange={setEsiLessonHrs} display={esiLessonHrs + 'h'} />
                <SliderRow label="Speaking Club (h/semana, total)"  min={0}   max={4}  step={0.5} value={esiScHrs}     onChange={setEsiScHrs}     display={esiScHrs + 'h'} />
                <SliderRow label="Média alunos no Speaking Club"    min={1}   max={6}  step={1}   value={esiScSize}    onChange={setEsiScSize}    display={esiScSize + ''} />
                <SliderRow label="Prep (h/semana)"                  min={0.5} max={5}  step={0.5} value={esiPrepHrs}   onChange={setEsiPrepHrs}   display={esiPrepHrs + 'h'} />
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card"><MCard title="Kickstart" margin={mKs.margin} badge="Grupo · 8 semanas · mín. 2 meses · max 5 alunos">
                  <DR label="Preço por aluno" value="R$147/mês" />
                  <DR label="Custo professor/aluno" value={fmt(mKs.cps)} cls="daq-neg" />
                  <DR label="Lucro bruto/aluno" value={fmt(mKs.gpps)} cls={sgn(mKs.gpps)} />
                  <DR label="Lucro bruto por cohort (5 alunos × 2 meses)" value={fmt(mKs.gpps * 5 * 2)} cls={sgn(mKs.gpps)} />
                </MCard></div>
                <div className="daq-card"><MCard title="Individual VIP" margin={mVip.margin} badge="Contrato 3 meses · máx 1 aluno">
                  <DR label="Preço" value="R$599/mo" />
                  <DR label="Horas totais/mês" value={mVip.h.toFixed(1) + 'h'} />
                  <DR label="Custo professor" value={fmt(mVip.cost)} cls="daq-neg" />
                  <DR label="Lucro bruto" value={fmt(mVip.gp)} cls={sgn(mVip.gp)} />
                </MCard></div>
                <div className="daq-card"><MCard title="Elite Squad — fase individual (meses 1-2)" margin={mEsI.margin} badge="Fase de onboarding">
                  <DR label="Preço" value="R$299/mo" />
                  <DR label="Aula individual/mês" value={mEsI.lessonHrsMonth.toFixed(1) + 'h'} />
                  <DR label="Speaking Club/mês (rateado)" value={mEsI.scHrsMonth.toFixed(1) + 'h'} />
                  <DR label="Prep/mês" value={mEsI.prepHrsMonth.toFixed(1) + 'h'} />
                  <DR label="Horas totais" value={mEsI.h.toFixed(1) + 'h'} />
                  <DR label="Custo professor" value={fmt(mEsI.cost)} cls="daq-neg" />
                  <DR label="Lucro bruto" value={fmt(mEsI.gp)} cls={sgn(mEsI.gp)} />
                </MCard></div>
                <div className="daq-card"><MCard title="Elite Squad — fase grupo (meses 3-12)" margin={mEsG.margin} badge="Contrato 12 meses">
                  <DR label="Preço" value="R$299/mo" />
                  <DR label="Slots/semana" value="4" />
                  <DR label="Média alunos/slot" value="3" />
                  <DR label="Custo professor/aluno" value={fmt(mEsG.cps)} cls="daq-neg" />
                  <DR label="Lucro bruto/aluno" value={fmt(mEsG.gpps)} cls={sgn(mEsG.gpps)} />
                </MCard></div>
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Comparação de margem — lucro bruto por aluno/mês</div>
                <div className="daq-chart" style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={marginData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="revenue" name="Receita"     fill={COLORS.green} radius={[4,4,0,0]} />
                      <Bar dataKey="cost"    name="Custo"       fill={COLORS.red}   radius={[4,4,0,0]} />
                      <Bar dataKey="gp"      name="Lucro bruto" fill={COLORS.blue}  radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="daq-alert daq-alert-green">ℹ <strong>Kickstart:</strong> produto grupo-only, lucrativo por si só (~29% margem no cenário padrão). A conversão para Elite Squad na semana 8 é lucro adicional, não o único motivo do produto existir.</div>
              <div className="daq-alert daq-alert-blue">ℹ <strong>Elite Squad meses 1–2:</strong> com o conteúdo já criado e reutilizado, a fase individual é praticamente neutra (~-R$16/aluno/mês). O contrato de 12 meses é uma proteção, não um subsídio.</div>
            </div>
          )}

          {/* LTV */}
          {tab === 'ltv' && (
            <div className="daq-page active">
              <div className="daq-page-title">LTV & contratos</div>
              <div className="daq-page-sub">Receita e lucro ao longo do contrato. Quando cada aluno se torna lucrativo?</div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Premissas</div>
                <SliderRow label="Taxa de cancelamento Elite Squad (%)" min={0} max={50}  step={1} value={lChurn} onChange={setLChurn} display={lChurn + '%'} />
                <SliderRow label="Mês típico de cancelamento"           min={2} max={10}  step={1} value={lCmo}   onChange={setLCmo}   display={'mês ' + lCmo} />
                <SliderRow label="Taxa de renovação VIP (%)"            min={0} max={100} step={5} value={lRenew} onChange={setLRenew} display={lRenew + '%'} />
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Kickstart LTV por aluno</div>
                  <DR label="Duração mínima"            value="2 meses" />
                  <DR label="Valor mínimo pago"         value={fmt(2 * 147)} />
                  <DR label="Lucro bruto (2 meses)"     value={fmt(ltvKs.full)} cls={sgn(ltvKs.full)} />
                  <DR label="Lucro por cohort de 5"     value={fmt(ltvKs.full * 5)} cls={sgn(ltvKs.full * 5)} />
                  <DR label="Valor extra com conversão ES" value="+ R$3.588 (contrato 12 meses)" cls="daq-pos" />
                  <div className="daq-alert daq-alert-green" style={{ marginTop: 10 }}>O Kickstart já é lucrativo sozinho (~R${Math.round(ltvKs.full)}/aluno em 2 meses). A conversão para Elite Squad na semana 8 é um bônus em cima do lucro do próprio Kickstart, não a única fonte de retorno.</div>
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">Elite Squad LTV por aluno</div>
                  <DR label="Valor do contrato (12 meses)"      value={fmt(12 * 299)} />
                  <DR label="Lucro bruto total (sem churn)"     value={fmt(ltvEs.full)} cls={sgn(ltvEs.full)} />
                  <DR label="Perda fase individual (meses 1–2)" value={fmt(mEsI.gp * 2)} cls={sgn(mEsI.gp * 2)} />
                  <DR label="Lucro fase grupo (meses 3–12)"     value={fmt(mEsG.gpps * 10)} cls={sgn(mEsG.gpps * 10)} />
                  <DR label="Break-even no"                     value={'Mês ' + ltvEs.be} cls="daq-warn" />
                  <DR label={`Lucro esperado (${lChurn}% churn)`} value={fmt(ltvEs.exp)} cls={sgn(ltvEs.exp)} />
                  <DR label="CAC máximo (30% do lucro)"         value={fmt(Math.max(0, ltvEs.exp * 0.3))} cls="daq-pos" />
                </div>
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Individual VIP LTV por aluno</div>
                  <DR label="Valor do contrato (3 meses)"  value={fmt(3 * 599)} />
                  <DR label="Lucro bruto (1 contrato)"     value={fmt(mVip.gp * 3)} cls={sgn(mVip.gp * 3)} />
                  <DR label="Taxa de renovação"            value={lRenew + '%'} />
                  <DR label="LTV combinado com renovações" value={fmt(mVip.gp * 3 * (1 + lRenew/100 + (lRenew/100)**2))} cls="daq-pos" />
                  <DR label="CAC máximo (30% do lucro)"   value={fmt(Math.max(0, mVip.gp * 3 * 0.3))} cls="daq-pos" />
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">Proteção contra cancelamento ES</div>
                  {[1, 2, 3, 5, 6, 9].map(mo => {
                    const paid = mo * 299;
                    const pen  = (12 - mo) * 299 * 0.2;
                    const gpR  = mo <= 2 ? mEsI.gp * mo : mEsI.gp * 2 + mEsG.gpps * (mo - 2);
                    return <DR key={mo} label={`Cancelamento após mês ${mo}`} value={`Pago: ${fmt(paid)} + Multa: ${fmt(pen)} = <strong>${fmt(paid + pen)}</strong> · Lucro: <span class="${sgn(gpR)}">${fmt(gpR)}</span>`} />;
                  })}
                </div>
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Elite Squad — lucro bruto cumulativo por aluno (12 meses)</div>
                <div className="daq-card-sub">A fase individual (meses 1-2) é negativa. A fase grupo recupera e acumula.</div>
                <div className="daq-chart" style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ltvEs.cum}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="mo" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Line type="monotone" dataKey="gp" name="Lucro cumulativo" stroke={COLORS.green} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* P&L */}
          {tab === 'pnl' && (
            <div className="daq-page active">
              <div className="daq-page-title">P&L tracker</div>
              <div className="daq-page-sub">O custo do professor é dirigido pelos slots comprometidos, não apenas pelos alunos inscritos.</div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Alunos este mês</div>
                  <SliderRow label="ES fase individual (alunos)"  min={0} max={10}    step={1}   value={esiN}   onChange={setEsiN}   display={esiN + ''} />
                  <SliderRow label="ES fase grupo (inscritos)"    min={0} max={40}    step={1}   value={esN}    onChange={setEsN}    display={esN + ''} />
                  <SliderRow label="ES slots semanais"            min={1} max={8}     step={1}   value={esSlots} onChange={setEsSlots} display={esSlots + ''} />
                  <SliderRow label="Kickstart grupos ativos"      min={0} max={8}     step={1}   value={ksG}    onChange={setKsG}    display={ksG + ''} />
                  <SliderRow label="Kickstart média alunos/grupo" min={1} max={5}     step={1}   value={ksF}    onChange={setKsF}    display={ksF + ''} />
                  <SliderRow label="VIP (máx 1 aluno)"           min={0} max={1}     step={1}   value={vipN}   onChange={setVipN}   display={vipN + ''} />
                  <SliderRow label="Receita legacy/mensais (R$)"  min={0} max={10000} step={100} value={legRev} onChange={setLegRev} display={fmtK(legRev)} />
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">Custos fixos mensais</div>
                  <SliderRow label="Salários (bruto)" min={0} max={15000} step={100} value={sal} onChange={setSal} display={fmtK(sal)} />
                  <SliderRow label="Marketing"        min={0} max={5000}  step={100} value={mkt} onChange={setMkt} display={fmtK(mkt)} />
                  <SliderRow label="Impostos"         min={0} max={3000}  step={50}  value={tax} onChange={setTax} display={fmtK(tax)} />
                  <SliderRow label="Outros"           min={0} max={2000}  step={50}  value={oth} onChange={setOth} display={fmtK(oth)} />
                </div>
              </div>

              <div className="daq-g5 daq-gap">
                {[
                  { label:'Receita total',   value: fmtK(pnl.tRev),   sub:'', color:'' },
                  { label:'Custo professor', value: fmtK(pnl.tTeach), sub: pnl.tRev>0?pct(pnl.tTeach/pnl.tRev*100)+' da receita':'', color:'daq-neg' },
                  { label:'Lucro bruto',     value: fmtK(pnl.gross),  sub:'', color: sgn(pnl.gross) },
                  { label:'Custos fixos',    value: fmtK(pnl.tFixed), sub:'', color:'daq-neg' },
                  { label:'Lucro líquido',   value: fmtK(pnl.net),    sub: pct(pnl.nm) + ' margem', color: sgn(pnl.net) },
                ].map(t => (
                  <div key={t.label} className="daq-tile">
                    <div className="daq-tile-label">{t.label}</div>
                    <div className={`daq-tile-value ${t.color}`}>{t.value}</div>
                    {t.sub && <div className="daq-tile-sub">{t.sub}</div>}
                  </div>
                ))}
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Waterfall de receita</div>
                <div className="daq-chart" style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pnlWaterfall}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Bar dataKey="v" name="Valor">
                        {pnlWaterfall.map((bar, i) => <Cell key={i} fill={bar.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">P&L histórico <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span></div>
                <div className="daq-chart" style={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dbPnl}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="revenue" name="Receita" fill="rgba(22,163,73,0.2)" stroke={COLORS.green} strokeWidth={2} radius={[4,4,0,0]} />
                      <Bar dataKey="profit"  name="Lucro"   radius={[4,4,0,0]}>
                        {dbPnl.map((row, i) => <Cell key={i} fill={row.profit >= 0 ? COLORS.blue : COLORS.red} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* SLOTS */}
          {tab === 'slots' && (
            <div className="daq-page active">
              <div className="daq-page-title">Slot utilization</div>
              <div className="daq-page-sub">Pagamos pelo slot independente de quantos alunos aparecem. Fill rate é a nossa maior alavanca operacional.</div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Configuração</div>
                <SliderRow label="Slots semanais comprometidos"    min={1}  max={8}  step={1} value={slSlots} onChange={setSlSlots} display={slSlots + ''} />
                <SliderRow label="Total de alunos inscritos"       min={1}  max={40} step={1} value={slStu}   onChange={setSlStu}   display={slStu + ''} />
                <SliderRow label="Taxa do professor (R$/h)"        min={25} max={80} step={1} value={slRate}  onChange={setSlRate}  display={'R$' + slRate} />
                <SliderRow label="Prep por hora de contato (min)"  min={10} max={90} step={5} value={slPrep}  onChange={setSlPrep}  display={slPrep + ' min'} />
              </div>

              <div className="daq-g4 daq-gap">
                {[
                  { label:'Média alunos/slot', value: slAvg.toFixed(1), sub: pct(Math.round(slAvg/5*100)) + ' de ocupação', color: slAvg<2?'daq-neg':slAvg<3?'daq-warn':'daq-pos' },
                  { label:'Receita mensal',    value: fmtK(slE.rev),    sub: slStu + ' × R$299', color:'' },
                  { label:'Custo slot (fixo)', value: fmtK(slE.sc),     sub:'independente da ocupação', color:'daq-neg' },
                  { label:'Lucro bruto',       value: fmtK(slE.gp),     sub: pct(slE.margin), color: sgn(slE.gp) },
                ].map(t => (
                  <div key={t.label} className="daq-tile">
                    <div className="daq-tile-label">{t.label}</div>
                    <div className={`daq-tile-value ${t.color}`}>{t.value}</div>
                    <div className="daq-tile-sub">{t.sub}</div>
                  </div>
                ))}
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Visualização de slots</div>
                <div style={{ display:'grid', gridTemplateColumns: `repeat(${Math.min(slSlots, 4)}, 1fr)`, gap: 10, marginBottom: 14 }}>
                  {Array.from({ length: slSlots }, (_, i) => {
                    const fl = Math.floor(slAvg), cl = Math.ceil(slAvg);
                    const wC = slStu - fl * slSlots;
                    const n  = i < wC ? cl : fl;
                    const col = n <= 1 ? COLORS.red : n <= 2 ? COLORS.amber : COLORS.green;
                    return (
                      <div key={i} style={{ border:'1px solid rgba(0,0,0,0.08)', borderRadius: 8, padding: '12px 10px', textAlign:'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: col }}>{n}</div>
                        <div style={{ fontSize: 10, color:'#9d9c98' }}>alunos</div>
                        <div style={{ fontSize: 10, color:'#9d9c98', marginBottom: 6 }}>Slot {i + 1}</div>
                        <div className="daq-pb-bg"><div className="daq-pb" style={{ width: pct(n/5*100), background: col }} /></div>
                      </div>
                    );
                  })}
                </div>
                {slE.gp < 0 && <div className="daq-alert daq-alert-red">Lucro bruto negativo. Precisamos de pelo menos <strong>{Math.ceil(slE.sc/299)} alunos</strong> só para cobrir o custo dos slots.</div>}
                {slAvg < 2  && <div className="daq-alert daq-alert-amber">Média {slAvg.toFixed(1)} alunos/slot é criticamente baixo. Considere reduzir para {Math.max(1,slSlots-1)} slots.</div>}
                {slAvg >= 4 && <div className="daq-alert daq-alert-green">Boa ocupação. Perto da capacidade — planeje o próximo slot quando matricular a próxima turma.</div>}
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Sensibilidade ao lucro — média de alunos por slot</div>
                <div className="daq-chart" style={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={fillData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="rev"  name="Receita"     fill={COLORS.green} radius={[4,4,0,0]} />
                      <Bar dataKey="cost" name="Custo slot"  fill={COLORS.red}   radius={[4,4,0,0]} />
                      <Bar dataKey="gp"   name="Lucro bruto" fill={COLORS.blue}  radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="daq-card">
                <div className="daq-card-title">Thresholds mínimos de ocupação</div>
                <DR label="Mínimo para cobrir custo de slots" value={`<strong>${Math.ceil(slE.sc/299)}</strong> alunos (${(Math.ceil(slE.sc/299)/slSlots).toFixed(1)} média/slot)`} />
                <DR label="Mínimo para 40% de margem bruta"  value={`<strong>${Math.ceil(slE.sc/0.6/299)}</strong> alunos`} />
                <DR label="Receita com slots cheios"          value={fmt(5*299*4) + '/mês (5 alunos × R$299 × 4 semanas)'} />
              </div>
            </div>
          )}

          {/* CAC */}
          {tab === 'cac' && (
            <div className="daq-page active">
              <div className="daq-page-title">Custo de aquisição (CAC)</div>
              <div className="daq-page-sub">Quanto custa adquirir cada aluno, e isso é justificado pelo LTV? O ratio que importa: CAC {'<'} 30% do LTV.</div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Gasto com marketing este mês (R$)</div>
                  <SliderRow label="Gasto total"     min={0} max={8000} step={100} value={cSpend} onChange={setCSpend} display={fmt(cSpend)} />
                  <hr />
                  <div className="daq-card-title">Novos alunos adquiridos</div>
                  <SliderRow label="Elite Squad"     min={0} max={10} step={1} value={cEs}  onChange={setCEs}  display={cEs + ''} />
                  <SliderRow label="Kickstart"       min={0} max={10} step={1} value={cKs}  onChange={setCKs}  display={cKs + ''} />
                  <SliderRow label="VIP (máx 1)"     min={0} max={1}  step={1} value={cVip} onChange={setCVip} display={cVip + ''} />
                  <SliderRow label="Legacy / mensal" min={0} max={10} step={1} value={cLeg} onChange={setCLeg} display={cLeg + ''} />
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">CAC vs LTV</div>
                  {cTotal === 0
                    ? <div className="daq-alert daq-alert-blue">Informe os alunos adquiridos para ver a análise.</div>
                    : [
                        ['Elite Squad',    blendCAC, esGP12, 12],
                        ['Kickstart',      blendCAC, ksGP2,   2],
                        ['Individual VIP', blendCAC, vipGP3,  3],
                      ].map(([name, cac, ltv, mos]) => {
                        const ratio   = ltv > 0 ? cac / ltv : 99;
                        const payback = ltv > 0 ? Math.ceil(cac / (ltv / mos)) : 99;
                        const cls     = ratio < 0.2 ? 'daq-badge-green' : ratio < 0.35 ? 'daq-badge-amber' : 'daq-badge-red';
                        const health  = ratio < 0.2 ? 'Saudável' : ratio < 0.35 ? 'Aceitável' : 'Alto';
                        return (
                          <div key={name} className="daq-row" style={{ alignItems:'flex-start', flexDirection:'column', gap: 4 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
                              <span className="daq-row-label">{name}</span>
                              <span className={`daq-badge ${cls}`}>{health}</span>
                            </div>
                            <div style={{ fontSize: 12, color:'#5c5b58' }}>CAC {fmt(cac)} · LTV {fmt(ltv)} · Payback ~{payback} meses</div>
                          </div>
                        );
                      })
                  }
                  <hr />
                  <DR label="CAC combinado"      value={fmt(blendCAC)} />
                  <DR label="Total novos alunos" value={cTotal + ''} />
                  <div className="daq-alert daq-alert-blue" style={{ marginTop: 10 }}>
                    <strong>Kickstart como funil:</strong> LTV real = R$294 + probabilidade de conversão × R$3.588.
                  </div>
                </div>
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Aquisição por mês — dados ao vivo <span className="daq-live" style={{ float:'right' }}><span className="daq-live-dot" />live</span></div>
                <div className="daq-chart" style={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={acqByMonth} barSize={28}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip {...tooltipStyle} />
                      <Bar dataKey="students" name="Novos alunos" fill={COLORS.green} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="daq-card">
                <div className="daq-card-title">Valor de indicação</div>
                <div className="daq-alert daq-alert-green" style={{ marginBottom: 0 }}>
                  <strong>1 indicação = R$2.000–R$5.000+ de LTV com custo zero de aquisição.</strong> Junior Ribeiro veio via indicação de aluno. Um incentivo estruturado — mesmo R$200 de crédito — custa menos de 5% do LTV de um contrato.
                </div>
              </div>
            </div>
          )}

          {/* PRICING */}
          {tab === 'pricing' && (
            <div className="daq-page active">
              <div className="daq-page-title">Simulador de preços & promoções</div>
              <div className="daq-page-sub">Teste mudanças de preço e descontos. "Preço mínimo para 40% de margem" é o nosso floor.</div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Kickstart</div>
                  <SliderRow label="Preço (R$/mês)"  min={80} max={400} step={5} value={prKs}  onChange={setPrKs}  display={'R$' + prKs} />
                  <SliderRow label="Alunos no grupo" min={1}  max={5}   step={1} value={prKsN} onChange={setPrKsN} display={prKsN + ''} />
                  <DR label="Lucro bruto/aluno" value={fmt(prKsE.gpps)} cls={sgn(prKsE.gpps)} />
                  <DR label="Lucro por cohort de 5 (2 meses)" value={fmt(prKsE.gpps * 5 * 2)} cls={sgn(prKsE.gpps)} />
                  <DR label="Margem" value={`<span class="daq-badge ${bCls(prKsE.margin)}">${pct(prKsE.margin)}</span>`} />
                  <DR label="Preço mínimo para 40%" value={fmt(prKsE.cps / 0.6)} />
                  <div className={`daq-alert ${prKs < prKsE.cps/0.6 ? 'daq-alert-amber' : 'daq-alert-green'}`} style={{ marginTop: 10 }}>
                    {prKs < prKsE.cps/0.6 ? `Preço ${fmt(prKsE.cps/0.6 - prKs)} abaixo do floor de 40%.` : `${fmt(prKs - prKsE.cps/0.6)} de margem acima do floor de 40%.`}
                  </div>
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">Elite Squad — fase grupo</div>
                  <SliderRow label="Preço (R$/mês)"     min={150} max={700} step={5} value={prEs}  onChange={setPrEs}  display={'R$' + prEs} />
                  <SliderRow label="Média alunos/slot"  min={1}   max={5}   step={1} value={prEsF} onChange={setPrEsF} display={prEsF + ''} />
                  <SliderRow label="Slots semanais"     min={1}   max={8}   step={1} value={prEsS} onChange={setPrEsS} display={prEsS + ''} />
                  <DR label="Lucro bruto/aluno" value={fmt(prEsE.gpps)} cls={sgn(prEsE.gpps)} />
                  <DR label="Margem" value={`<span class="daq-badge ${bCls(prEsE.margin)}">${pct(prEsE.margin)}</span>`} />
                  <DR label="Preço mínimo para 40%" value={fmt(prEsE.cps / 0.6)} />
                  <div className={`daq-alert ${prEs < prEsE.cps/0.6 ? 'daq-alert-amber' : 'daq-alert-green'}`} style={{ marginTop: 10 }}>
                    {prEs < prEsE.cps/0.6 ? `Preço ${fmt(prEsE.cps/0.6 - prEs)} abaixo do floor de 40%.` : `${fmt(prEs - prEsE.cps/0.6)} de margem acima do floor de 40%.`}
                  </div>
                </div>
              </div>

              <div className="daq-g2 daq-gap">
                <div className="daq-card">
                  <div className="daq-card-title">Individual VIP</div>
                  <SliderRow label="Preço (R$/mês)" min={300} max={1500} step={10} value={prVip} onChange={setPrVip} display={'R$' + prVip} />
                  <DR label="Lucro bruto" value={fmt(prVipE.gp)} cls={sgn(prVipE.gp)} />
                  <DR label="Margem" value={`<span class="daq-badge ${bCls(prVipE.margin)}">${pct(prVipE.margin)}</span>`} />
                  <DR label="Preço mínimo para 40%" value={fmt(prVipE.cost / 0.6)} />
                  <div className={`daq-alert ${prVip < prVipE.cost/0.6 ? 'daq-alert-amber' : 'daq-alert-green'}`} style={{ marginTop: 10 }}>
                    {prVip < prVipE.cost/0.6 ? `Preço ${fmt(prVipE.cost/0.6 - prVip)} abaixo do floor de 40%.` : `${fmt(prVip - prVipE.cost/0.6)} de margem acima do floor de 40%.`}
                  </div>
                </div>
                <div className="daq-card">
                  <div className="daq-card-title">Testador de promoção</div>
                  <SliderRow label="Desconto %" min={0} max={50} step={5} value={prDisc} onChange={setPrDisc} display={prDisc + '%'} />
                  <select value={prSvc} onChange={e => setPrSvc(e.target.value)} style={{ marginBottom: 14 }}>
                    <option value="ks">Kickstart</option>
                    <option value="es">Elite Squad</option>
                    <option value="vip">Individual VIP</option>
                  </select>
                  <DR label={prDs.name + ' com ' + prDisc + '% de desconto'} value={fmt(dP) + '/mês'} />
                  <DR label="Margem após desconto" value={`<span class="daq-badge ${bCls(dP>0?dGP/dP*100:-100)}">${pct(dP>0?dGP/dP*100:0)}</span>`} />
                  <DR label="Viável?" value={`<span class="${sgn(dGP)}">${dGP >= 0 ? 'Sim — ainda lucrativo' : 'Não — perda por aluno'}</span>`} />
                </div>
              </div>
            </div>
          )}

          {/* BREAK-EVEN */}
          {tab === 'breakeven' && (
            <div className="daq-page active">
              <div className="daq-page-title">Break-even</div>
              <div className="daq-page-sub">Quantos alunos precisamos para cobrir todos os custos fixos?</div>

              <div className="daq-card daq-gap">
                <SliderRow label="Custos fixos mensais totais (R$)"  min={3000} max={25000} step={500} value={beFixed} onChange={setBeFixed} display={fmtK(beFixed)} />
                <SliderRow label="Slots ES comprometidos por semana" min={1}    max={8}     step={1}   value={beSlots} onChange={setBeSlots} display={beSlots + ''} />
              </div>

              <div className="daq-g4 daq-gap">
                {[
                  { label:'Kickstart',                   value: beKS  === Infinity ? '∞' : beKS  + ' grupos', sub:'cada grupo = 5 alunos × R$147 × 2 meses', color:'' },
                  { label:'ES slots cheios (5/slot)',     value: beESF === Infinity ? '∞' : beESF + ' alunos', sub:'melhor caso: slots 100% preenchidos', color:'' },
                  { label:'ES slots meio-cheios (2–3/slot)', value: beESH === Infinity ? '∞' : beESH + ' alunos', sub:'cenário realista', color: beESH > 30 ? 'daq-neg' : 'daq-warn' },
                  { label:'VIP (máx 1 aluno)',            value: beVIP === Infinity ? '∞' : beVIP + ' alunos', sub:'limitado a 1 — não escala sozinho', color:'' },
                ].map(t => (
                  <div key={t.label} className="daq-tile">
                    <div className="daq-tile-label">{t.label}</div>
                    <div className={`daq-tile-value ${t.color}`} style={{ fontSize: 18 }}>{t.value}</div>
                    <div className="daq-tile-sub">{t.sub}</div>
                  </div>
                ))}
              </div>

              <div className="daq-card daq-gap">
                <div className="daq-card-title">Margem de contribuição cumulativa vs custos fixos</div>
                <div style={{ display:'flex', gap: 14, marginBottom: 10, flexWrap:'wrap', fontSize: 12, color:'#5c5b58' }}>
                  {[['#16a349','Kickstart'],['#2563eb','ES (slots cheios)'],['#7c3aed','VIP'],['#dc2626','Custos fixos']].map(([c,l]) => (
                    <span key={l} style={{ display:'flex', alignItems:'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, background: c, borderRadius: 2, display:'inline-block' }} />{l}
                    </span>
                  ))}
                </div>
                <div className="daq-chart" style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={beLineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="x" tick={{ fontSize: 11 }} label={{ value:'Alunos inscritos', position:'insideBottom', offset:-5, fontSize:11 }} height={36} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtK(v)} />
                      <Tooltip {...tooltipStyle} formatter={v => fmt(v)} />
                      <Line type="monotone" dataKey="ks"    name="Kickstart"    stroke={COLORS.green}  strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="esF"   name="ES cheio"     stroke={COLORS.blue}   strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="vip"   name="VIP"          stroke={COLORS.purple} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="fixed" name="Custos fixos" stroke={COLORS.red}    strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="daq-alert daq-alert-blue">
                Com <strong>{beSlots} slots/semana</strong> comprometidos, precisamos de pelo menos <strong>{Math.ceil(esGrpEcon(beSlots, 5, 299, rate, pH, aH, rH, cH).sc / 299)} alunos</strong> só para cobrir o custo de professor dos slots.
              </div>
              {beESH > beESF * 1.5 && (
                <div className="daq-alert daq-alert-amber">
                  Slots meio-cheios precisam de <strong>{beESH - beESF} alunos a mais</strong> para o break-even vs slots cheios.
                </div>
              )}
              <div className="daq-alert daq-alert-blue">
                VIP precisa de apenas <strong>{beVIP} aluno{beVIP !== 1 ? 's' : ''}</strong> para cobrir todos os custos fixos — mas temos capacidade máxima de 1.
              </div>
            </div>
          )}

        </main>
      </div>
    </>
  );
}
